import { useState } from 'react';
import type { PulseItem } from '../domain/types';
import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, R, STAGE_CHIP, font } from '../theme/tokens';
import { pulseActions, roleOf, type PulseAction } from '../state/selectors';
import { ago } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { Avatar, Mono, Photo, Tag } from './ui';

const btnBase = { height: 44, padding: '0 16px', borderRadius: R.pill, font: font(600, 13), transition: 'all .2s', whiteSpace: 'nowrap' as const };

/** One UGAIN Pulse item on the night band. */
export function PulseCard({ item }: { item: PulseItem }) {
  const { state: { data }, act } = useApp();
  const nowMs = useNow();
  const [thread, setThread] = useState(false);
  const [draft, setDraft] = useState('');
  const a = data.activities[item.activityId];
  const p = data.ambassadors[item.authorId];
  if (!a || !p) return null;

  const role = roleOf(data, a.id);
  const isMe = item.authorId === data.me;
  const [stLabel, stBg, stFg] = item.kind === 'help' ? ['HELP WANTED', C.amberBg, C.amber] : STAGE_CHIP[a.stage];
  const mine = role ? (a.organiserId === data.me ? 'YOUR ACTIVITY' : `YOU: ${role.toUpperCase()}`) : null;
  const openActivity = () => navigate({ name: 'activity', id: a.id });

  const spec = (k: PulseAction): { label: string; on: boolean; onClick: () => void } => {
    switch (k) {
      case 'follow': {
        const on = Boolean(data.followedActivities[a.id]);
        return { label: on ? 'Following ✓' : 'Follow activity', on, onClick: () => act.toggleFollowActivity(a.id) };
      }
      case 'help': {
        const on = Boolean(data.offeredHelp[a.id]);
        return { label: on ? 'Offered ✓' : 'Offer help', on, onClick: () => act.toggleOffer(a.id) };
      }
      case 'share':
        return { label: 'Share campaign', on: false, onClick: () => a.shareKitId && act.openSheet({ kind: 'quickShare', kitId: a.shareKitId }) };
      default:
        return { label: 'Open activity', on: false, onClick: openActivity };
    }
  };
  const actions = pulseActions(data, item).map(spec);

  return (
    <article className="fade" style={{ background: C.dark1, borderRadius: R.pulse, overflow: 'hidden' }}>
      <div style={{ padding: '14px 14px 0', display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
        <button type="button" onClick={() => (isMe ? navigate({ name: 'me' }) : navigate({ name: 'profile', id: p.id }))}
          style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 10, alignItems: 'center', border: 0, background: 'transparent', padding: 0, textAlign: 'left', color: C.white, minWidth: 0 }}>
          <Avatar person={p} size={36} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ font: font(600, 14) }}>{isMe ? `${p.name} (you)` : p.name}</span>
            <Mono size={10} color={C.mutedDark} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title.toUpperCase()}</Mono>
          </span>
        </button>
        <Mono size={10} color={C.mutedDark}>{ago(item.createdAt, nowMs)}</Mono>
      </div>
      <p style={{ margin: '12px 14px 0', font: font(600, 18, 1.25), letterSpacing: '-.015em', color: C.white }}>{item.statement}</p>
      {item.photo && (
        <button type="button" onClick={openActivity} aria-label={`Open ${a.name}`}
          style={{ display: 'block', width: 'calc(100% - 28px)', margin: '12px 14px 0', height: 170, border: 0, padding: 0, borderRadius: R.thumb, overflow: 'hidden', background: C.night }}>
          <Photo id={item.photo} pad={18} sizes="(max-width: 480px) 90vw, 440px" />
        </button>
      )}
      <button type="button" onClick={openActivity} style={{ margin: '12px 14px 0', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', border: 0, background: 'transparent', padding: 0, textAlign: 'left' }}>
        <Tag label={stLabel} bg={stBg} fg={stFg} />
        <span style={{ font: font(500, 12), color: C.metal }}>{a.name} ›</span>
        {mine && <Tag label={mine} bg={C.mark} fg={C.ink} />}
        {item.visibility === 'team' && <Tag label="TEAM ONLY" bg={C.dark3} fg={C.metal} />}
      </button>
      <div style={{ padding: '12px 14px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {actions.map((s, i) => (
          <button key={s.label} type="button" onClick={s.onClick} aria-pressed={s.on || undefined} className="press"
            style={{ ...btnBase, ...(i === 0 ? { background: s.on ? C.mark : C.white, color: C.ink, border: `1px solid ${s.on ? C.mark : C.white}` } : { background: s.on ? C.dark3 : 'transparent', color: C.white, border: `1px solid ${C.darkBorder}` }) }}>
            {s.label}
          </button>
        ))}
        {/* Message opens the team chat; outside the team there is no messaging yet, so no button. */}
        {(isMe || role) && (
          <button type="button" className="press" onClick={() => navigate({ name: 'collab', id: a.id })}
            style={{ ...btnBase, marginLeft: 'auto', padding: '0 14px', background: 'transparent', color: C.white, border: `1px solid ${C.darkBorder}` }}>
            Message
          </button>
        )}
      </div>
      <div style={{ padding: '10px 14px 14px' }}>
        <button type="button" onClick={() => setThread((t) => !t)} aria-expanded={thread}
          style={{ border: 0, background: 'transparent', padding: '8px 0', color: C.mutedDark, font: font(600, 12), minHeight: 36 }}>
          {item.comments.length ? `${item.comments.length} comment${item.comments.length > 1 ? 's' : ''}` : 'Comment'} {thread ? '▴' : '▾'}
        </button>
        {thread && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            {item.comments.map((c) => {
              const who = data.ambassadors[c.authorId];
              return (
                <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 8, alignItems: 'start' }}>
                  {who && <Avatar person={who} size={28} />}
                  <span style={{ font: font(400, 13, 1.4), color: C.metal }}>
                    <b style={{ color: C.white, fontWeight: 600 }}>{c.authorId === data.me ? 'You' : who?.name.split(' ')[0]}</b> {c.text}
                  </span>
                </div>
              );
            })}
            <form onSubmit={(e) => { e.preventDefault(); act.comment(item.id, draft); setDraft(''); }} style={{ display: 'flex', gap: 6 }}>
              <input aria-label="Add a comment" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={280} placeholder="Add a comment"
                style={{ flex: 1, minWidth: 0, height: 44, borderRadius: R.pill, border: `1px solid ${C.darkBorder}`, background: C.dark2, color: C.white, padding: '0 14px', font: font(400, 16) }} />
              <button type="submit" disabled={!draft.trim()} style={{ ...btnBase, background: draft.trim() ? C.white : C.dark3, color: draft.trim() ? C.ink : C.mutedDark, border: 0 }}>Post</button>
            </form>
          </div>
        )}
      </div>
    </article>
  );
}
